import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Users, Settings, LogOut, Menu, X,
  Globe, Package, MessageSquare, Bell, ChevronDown, Calendar, UserPlus, Layers, DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { Toaster } from '../components/ui/sonner';
import { useAuth } from '../context/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a01f8d12-1bde-4fd2-8a5f-4dafd263008a/artifacts/d665bj3h_Global%20Wings%20fond%20noir.png";

const sidebarLinks = [
  { name: 'Dashboard', icon: LayoutDashboard, href: '/admin' },
  { name: 'Demandes Visa', icon: Globe, href: '/admin/visa-applications' },
  { name: 'Rendez-vous', icon: Calendar, href: '/admin/appointments' },
  { name: 'Prospects', icon: UserPlus, href: '/admin/prospects' },
  { name: 'Offres', icon: Package, href: '/admin/offers' },
  { name: 'Prix & Visas', icon: DollarSign, href: '/admin/visa-prices', adminOnly: true },
  { name: 'Services', icon: Layers, href: '/admin/services', adminOnly: true },
  { name: 'Demandes Services', icon: MessageSquare, href: '/admin/service-requests' },
  { name: 'Utilisateurs', icon: Users, href: '/admin/users', adminOnly: true }
];

export const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredLinks = sidebarLinks.filter(link => !link.adminOnly || isAdmin());

  return (
    <div className="min-h-screen bg-[#020617] text-white">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0F172A] border-r border-white/10 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          <Link to="/">
            <img src={LOGO_URL} alt="Global Wings" className="h-20" />
          </Link>
          <button 
            className="lg:hidden text-gray-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {filteredLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                location.pathname === link.href
                  ? 'bg-sky-500/20 text-sky-400'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
              data-testid={`sidebar-${link.name.toLowerCase().replace(' ', '-')}`}
            >
              <link.icon className="w-5 h-5" />
              {link.name}
            </Link>
          ))}
        </nav>

        {/* Bottom Links */}
        <div className="absolute bottom-4 left-0 right-0 px-4">
          <Link to="/" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <Globe className="w-5 h-5" />
            Voir le site
          </Link>
          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="h-16 bg-[#0F172A]/50 backdrop-blur border-b border-white/10 flex items-center justify-between px-4 sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden text-gray-400 hover:text-white"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-sidebar-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold">
              {sidebarLinks.find(l => l.href === location.pathname)?.name || 'Dashboard'}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            {/* Notifications */}
            <button className="relative p-2 text-gray-400 hover:text-white">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-sky-500 rounded-full"></span>
            </button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 text-gray-300 hover:text-white" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center">
                    <span className="text-sm font-medium text-sky-400">
                      {user?.full_name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden sm:block text-sm">{user?.full_name}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#1E293B] border-white/10">
                <DropdownMenuItem className="text-gray-400">
                  <span className="text-xs uppercase tracking-wide">{user?.role}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/')} className="text-white hover:bg-white/10">
                  Voir le site
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-400 hover:bg-red-500/10">
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Toaster position="top-right" theme="dark" />
    </div>
  );
};

export default AdminLayout;
